<template>
  <section class="projects" data-aos="fade-up">
    <h2 class="section-title">Projetos em destaque</h2>
    <p class="section-subtitle">
      Alguns dos trabalhos que refletem meu dia a dia como desenvolvedor.
    </p>

    <div class="projects-grid">
      <article
        v-for="project in projects"
        :key="project.title"
        class="project-card"
        data-aos="fade-up"
        data-aos-delay="120"
      >
        <div class="project-header">
          <span class="project-type">{{ project.type }}</span>
          <span class="project-tag">{{ project.tech }}</span>
        </div>

        <h3 class="project-title">{{ project.title }}</h3>
        <p class="project-description">
          {{ project.description }}
        </p>

        <ul class="project-highlights">
          <li v-for="(item, idx) in project.highlights" :key="idx">
            {{ item }}
          </li>
        </ul>

        <div class="project-footer">
          <div class="project-status">
            <span class="status-dot"></span>
            <span>{{ project.status }}</span>
          </div>

          <div class="project-links">
            <a
              v-if="project.github"
              :href="project.github"
              target="_blank"
              rel="noopener"
            >
              GitHub
            </a>
            <a
              v-if="project.demo"
              :href="project.demo"
              target="_blank"
              rel="noopener"
            >
              Ver online
            </a>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
const projects = [
  {
    title: "Integração Bancária · Santander e Bradesco",
    type: "Sistema interno",
    tech: "C# · .NET · APIs",
    description:
      "DLL em C# consumida por sistema legado em VB6 para consulta de saldo e extrato bancário via APIs oficiais.",
    highlights: [
      "Autenticação via certificado digital",
      "Integração com múltiplos bancos",
      "Geração de payloads Pix EMV-QRCPS-MPM",
    ],
    status: "Em uso em ambiente produtivo",
    github: "https://github.com/LeandroVPrado", // ajuste se tiver repo específico
    demo: null,
  },
  {
    title: 'Site institucional "O Porteiro Eletrônico"',
    type: "Site institucional",
    tech: "Vue.js · PHP · MySQL",
    description:
      "Site moderno para empresa de portaria eletrônica, com foco em apresentação de serviços e conversão via contato.",
    highlights: [
      "SPA em Vue.js com rotas",
      "Backend PHP com painel administrativo",
      "Formulário de contato integrado",
    ],
    status: "Projeto em evolução contínua",
    github: "https://github.com/LeandroVPrado",
    demo: null,
  },
  {
    title: "EstruturaMaterialize · Painel interno",
    type: "Sistema interno",
    tech: "ASP.NET Core · SQL Server",
    description:
      "Painel administrativo com dashboards, consultas de vendas, aniversariantes e automações internas.",
    highlights: [
      "Frontend inspirado em Material Design",
      "Filtros avançados e consultas otimizadas",
      "Integrações com WhatsApp e rotinas automáticas",
    ],
    status: "Em uso e recebendo melhorias",
    github: "https://github.com/LeandroVPrado",
    demo: null,
  },
  {
    title: "Prado's Desenvolvimento Web · Portfólio",
    type: "Portfólio",
    tech: "Vue.js",
    description:
      "Este próprio site, desenvolvido como portfólio profissional para apresentação de serviços como freelancer.",
    highlights: [
      "Tema dark tech com neon",
      "Animações com AOS + GSAP",
      "Focado em performance e conversão",
    ],
    status: "Sempre em melhoria",
    github: "https://github.com/LeandroVPrado",
    demo: "https://pradosweb.com.br",
  },
];
</script>

<style scoped>
.projects {
  padding: 2.2 0 3rem;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;
}

/* Card */
.project-card {
  border-radius: var(--pw-radius-xl);
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: radial-gradient(circle at 0 0, #020617, #020617 40%, #020617);
  padding: 1.2rem 1.2rem 1rem;
  box-shadow: var(--pw-shadow-soft);
  position: relative;
  overflow: hidden;
}

.project-card::before {
  content: "";
  position: absolute;
  inset: -40%;
  background: radial-gradient(
    circle at 0 0,
    rgba(34, 211, 238, 0.16),
    transparent 55%
  );
  opacity: 0.4;
  pointer-events: none;
}

/* Conteúdo */
.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.65rem;
}

.project-type {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: rgba(148, 163, 184, 0.95);
}

.project-tag {
  font-size: 0.75rem;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  border: 1px solid rgba(125, 211, 252, 0.7);
  background: linear-gradient(
    to right,
    rgba(34, 211, 238, 0.12),
    rgba(139, 92, 246, 0.3)
  );
}

.project-title {
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
}

.project-description {
  margin: 0 0 0.7rem;
  font-size: 0.9rem;
  color: var(--pw-muted);
}

.project-highlights {
  margin: 0 0 0.7rem;
  padding-left: 1.05rem;
  font-size: 0.85rem;
  color: rgba(209, 213, 219, 0.95);
}

.project-highlights li + li {
  margin-top: 0.15rem;
}

/* Rodapé */
.project-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
}

.project-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: rgba(190, 242, 100, 0.9);
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.9);
}

.project-links {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.project-links a {
  font-size: 0.8rem;
  color: rgba(191, 219, 254, 0.95);
  text-decoration: none;
  position: relative;
  padding-bottom: 0.05rem;
}


.project-links a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 1px;
  background: linear-gradient(to right, #22d3ee, #8b5cf6);
  transition: width 0.2s ease-out;
}

.project-links a:hover::after {
  width: 100%;
}

/* Responsivo */
@media (max-width: 960px) {
  .projects-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>